import React, { useReducer, useRef, useState, useCallback, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { io } from 'socket.io-client'
import { resolveChatServerUrl } from './chatServerUrl'
import curry from 'utils/curry'
import { chatService, formatChatHeadersByUserId, getEventIdForReceiverIds } from 'features/Chat'
import {
	getChatHeadersByUID,
	chatHeaderMarkedRead,
	chatHeaderUpserted,
	selectChatHeaders,
} from 'slices/chatSlice'
import type { IChatState, IChatInternalActions, IChatActions, IReadStateRow } from './Chat.types'
import { useChatPopup } from './ChatPopupContext'
import { uploadChatAttachment, kindForFile } from './utils/uploadChatAttachment'

// Provide the current auth token to the chat server on connect/reconnect.
// Read from persisted userInfo (the socket is created at module load, before
// Redux is available). The chat server only enforces this once JWT_SECRET is
// configured there, so sending it is safe/additive until then.
const getSocketAuth = (cb: (data: { token?: string }) => void) => {
	let token: string | undefined
	try {
		const stored = localStorage.getItem('userInfo')
		token = stored ? JSON.parse(stored)?.token : undefined
	} catch (e) {
		token = undefined
	}
	cb({ token })
}

/**
 * Chat server origin, resolved at RUNTIME rather than baked into the bundle.
 *
 * `window.__MDT_CONFIG__` comes from `/config.js`, which the page loads in its
 * head before this module runs, so the value is already here when the socket
 * below is created. See `backend/utilities/runtimeConfig.js`.
 *
 * `REACT_APP_CHAT_URL` is kept BELOW it as a fallback rather than removed. It
 * is still the only lever for a build served by something that does not run
 * our API, and dropping it would break such a deployment silently. Runtime
 * config wins because it is the one a deploy can correct without rebuilding.
 *
 * The final two fallbacks preserve the original behaviour exactly when nothing
 * is configured, so an existing deployment is unaffected until it opts in.
 *
 * Why this mattered enough to change: CRA inlines `REACT_APP_*` at build time,
 * so a build with the wrong value could not be corrected without rebuilding
 * and nothing announced it. It also forced CI to build the frontend twice on
 * every pull request, because the integration job needs a localhost chat URL
 * while the `frontend` job must mirror the Render build exactly. MYD-458.
 */
declare global {
	interface Window {
		__MDT_CONFIG__?: { chatUrl?: string }
	}
}

const CHAT_SERVER_URL = resolveChatServerUrl({
	runtime: typeof window !== 'undefined' ? window.__MDT_CONFIG__?.chatUrl : undefined,
	buildTime: process.env.REACT_APP_CHAT_URL,
	nodeEnv: process.env.NODE_ENV,
})

export const socket = io(CHAT_SERVER_URL, { auth: getSocketAuth })

socket.on('connect', () => {
	console.log(`ChatContext|connect|socket.id=${socket.id}`)
})

/**
 * Room key for a portfolio chat. This one string is simultaneously the Mongo
 * `roomId`, the socket.io room name, the `chatMessages` cache key, and
 * `currentEventId`, so the four can never drift.
 *
 * Keep in sync with `portfolioRoomId` in backend/utilities/chatRooms.js.
 */
export const portfolioRoomKey = (portfolioId: string) => `portfolio:${portfolioId}`

/**
 * A user's own socket room, where a new conversation's first message arrives.
 * Socket only: no message is stored under it. Keep in sync with `userRoom` in
 * chat-server/relay.js.
 */
export const userChatRoomKey = (userId: string) => `user:${userId}`

/**
 * The `chatMessages` cache key a persisted message belongs to.
 *
 * The same string is the socket room and, for rooms, the Mongo scope: a
 * portfolio's namespaced `roomId`, a project's id, or a DM thread's header id.
 * Keep in sync with `roomForMessage` in chat-server/index.js.
 */
export const cacheKeyForMessage = (message: any): string => {
	if (!message) return ''
	if (message.roomId) return message.roomId
	if (message.eventId) return message.eventId
	if (message.projectid) return String(message.projectid._id || message.projectid)
	return String(message._id)
}

const newClientId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`

const STATE: IChatState = {
	sample: 'Sample',
	socket,
	userId: '',
	chatHeaders: [],
	chatMessages: {},
	// Two stored thread pointers, one per scope. Keeping them separate is what
	// lets the popup open over a project chat page without either one losing
	// its selection. `currentEventId` below is computed from these.
	roomEventId: '',
	globalEventId: '',
	currentProjectId: '',
	currentPortfolioId: '',
	currentReceiverIds: [],
	chatInput: '',
	// Media chosen but not yet sent. Cleared when the message goes out.
	pendingAttachments: [],
	startNewChat: false,
	isLoading: false,
	isSendingMessage: false,
	onlineUserIds: [],
	readStateByEventId: {},
	isSocketConnected: socket.connected,

	// computed state
	currentEventId: '',
	chatScope: 'room',
	currentMessages: [],
	hasCurrentMessages: undefined,
	shouldDisplayChatInput: undefined,
	currentChatHeader: undefined,
}

const ACTIONS = {
	setSample: 'setSample',
	// No setChatHeaders: headers live in the `chatHeaders` Redux slice, which
	// the unread badge also reads. Two sources meant two answers.
	setChatMessages: 'setChatMessages',
	// Per-thread message writes. These exist because every caller used to build
	// the next map from `state.chatMessages` captured at render time. Sending a
	// message does two writes either side of an await — the optimistic append,
	// then the reconcile — and the second one spread a snapshot taken before
	// the first, so the sent message appeared and then vanished until a reload.
	// Merging inside the reducer means each write sees the live state.
	setChatMessagesForEvent: 'setChatMessagesForEvent',
	appendChatMessage: 'appendChatMessage',
	prependChatMessages: 'prependChatMessages',
	replaceChatMessage: 'replaceChatMessage',
	replaceMessageById: 'replaceMessageById',
	patchChatMessage: 'patchChatMessage',
	setCurrentEventId: 'setCurrentEventId',
	setGlobalEventId: 'setGlobalEventId',
	setCurrentProjectId: 'setCurrentProjectId',
	setCurrentPortfolioId: 'setCurrentPortfolioId',
	setCurrentReceiverIds: 'setCurrentReceiverIds',
	setChatInput: 'setChatInput',
	setPendingAttachments: 'setPendingAttachments',
	setStartNewChat: 'setStartNewChat',
	setIsLoading: 'setIsLoading',
	setIsSendingMessage: 'setIsSendingMessage',
	setOnlineUserIds: 'setOnlineUserIds',
	setReadStateByEventId: 'setReadStateByEventId',
	setIsSocketConnected: 'setIsSocketConnected',
}

/*
  REDUCER
*/
const reducer = (state: IChatState, action: { type: string; payload: any }) => {
	const { type, payload } = action

	if (type === ACTIONS.setSample) return { ...state, sample: payload }
	if (type === ACTIONS.setChatMessages) return { ...state, chatMessages: payload }

	/** Replaces the whole message list for one thread. */
	if (type === ACTIONS.setChatMessagesForEvent) {
		const { eventId, messages } = payload
		return { ...state, chatMessages: { ...state.chatMessages, [eventId]: messages } }
	}

	/** Adds one message, ignoring a document already present. */
	if (type === ACTIONS.appendChatMessage) {
		const { eventId, message } = payload
		const existing = state.chatMessages[eventId] || []
		// The POST response and the socket echo can carry the same document,
		// and a re-render can replay a send, so appends are deduped on both ids.
		const already = existing.some(
			m =>
				(message.clientId && m.clientId === message.clientId) ||
				(message._id && m._id === message._id),
		)
		if (already) return state
		return {
			...state,
			chatMessages: { ...state.chatMessages, [eventId]: [...existing, message] },
		}
	}

	/** Adds a page of older messages to the front, skipping any already held. */
	if (type === ACTIONS.prependChatMessages) {
		const { eventId, messages } = payload
		const existing = state.chatMessages[eventId] || []
		const seen = new Set(existing.map(m => m._id))
		const fresh = (messages || []).filter(m => !seen.has(m._id))
		if (fresh.length === 0) return state
		return {
			...state,
			chatMessages: { ...state.chatMessages, [eventId]: [...fresh, ...existing] },
		}
	}

	/** Swaps an optimistic message for the persisted one. */
	if (type === ACTIONS.replaceChatMessage) {
		const { eventId, clientId, message } = payload
		const existing = state.chatMessages[eventId] || []
		const found = existing.some(m => m.clientId === clientId)
		// Append when the optimistic row is not there: the thread may have been
		// refetched mid-send. Dropping the message instead is what the bug this
		// action replaces actually did.
		const next = found
			? existing.map(m => (m.clientId === clientId ? message : m))
			: [...existing, message]
		return { ...state, chatMessages: { ...state.chatMessages, [eventId]: next } }
	}

	/**
	 * Replaces a persisted message in place, matched on _id. Used for edits and
	 * reactions, whose updates arrive both from the API response and, for the
	 * other side of the conversation, over the socket.
	 */
	if (type === ACTIONS.replaceMessageById) {
		const { eventId, message } = payload
		const existing = state.chatMessages[eventId] || []
		if (!existing.some(m => String(m._id) === String(message._id))) return state
		return {
			...state,
			chatMessages: {
				...state.chatMessages,
				[eventId]: existing.map(m =>
					String(m._id) === String(message._id) ? { ...m, ...message } : m,
				),
			},
		}
	}

	/** Updates fields on one in-flight message, e.g. its delivery status. */
	if (type === ACTIONS.patchChatMessage) {
		const { eventId, clientId, patch } = payload
		const existing = state.chatMessages[eventId] || []
		return {
			...state,
			chatMessages: {
				...state.chatMessages,
				[eventId]: existing.map(m => (m.clientId === clientId ? { ...m, ...patch } : m)),
			},
		}
	}
	// Named for the room scope it writes. The exported action keeps the old
	// name so ProjectChat/PortfolioChat/LocationChat need no changes.
	if (type === ACTIONS.setCurrentEventId) return { ...state, roomEventId: payload }
	if (type === ACTIONS.setGlobalEventId) return { ...state, globalEventId: payload }
	if (type === ACTIONS.setCurrentProjectId) return { ...state, currentProjectId: payload }
	if (type === ACTIONS.setCurrentPortfolioId) return { ...state, currentPortfolioId: payload }
	if (type === ACTIONS.setCurrentReceiverIds) return { ...state, currentReceiverIds: payload }
	if (type === ACTIONS.setChatInput) return { ...state, chatInput: payload }
	if (type === ACTIONS.setPendingAttachments) return { ...state, pendingAttachments: payload }
	if (type === ACTIONS.setStartNewChat) return { ...state, startNewChat: payload }
	if (type === ACTIONS.setIsLoading) return { ...state, isLoading: payload }
	if (type === ACTIONS.setIsSendingMessage) return { ...state, isSendingMessage: payload }
	if (type === ACTIONS.setOnlineUserIds) return { ...state, onlineUserIds: payload }
	if (type === ACTIONS.setReadStateByEventId) return { ...state, readStateByEventId: payload }
	if (type === ACTIONS.setIsSocketConnected) return { ...state, isSocketConnected: payload }

	return state
}

type Context = { state: IChatState; actions: any } | undefined
const ChatContext = React.createContext<Context>(undefined)

const ChatProvider = ({ children }: { children: React.ReactNode }) => {
	const chatInputRef = useRef<HTMLInputElement>(null)
	// Uploads resolve out of order and after several renders, so the update path
	// reads the live list from a ref. Reading `state.pendingAttachments` inside
	// those callbacks would capture the list as it was when the upload started
	// and drop every attachment that finished in the meantime.
	const pendingAttachmentsRef = useRef<any[]>([])
	const autocompleteInputRef = useRef<HTMLInputElement>(null)
	const messagesEndRef = useRef<HTMLDivElement>(null)
	// Thread rooms this socket has already joined. A Set rather than a boolean
	// so a thread created later in the session is joined too.
	const joinedChatRooms = useRef<Set<string>>(new Set())
	// Bumped on every socket connect, so the room-join effect re-runs.
	const [socketGeneration, setSocketGeneration] = useState(0)

	const location = useLocation()
	const reduxDispatch = useDispatch()

	/**
	 * Which chat the user is actually looking at.
	 *
	 * The global chat is a popup that can be opened from ANY screen, including
	 * a project or portfolio page. Without this, the URL sniff below would put
	 * a room in scope and `sendChatMessage` would write a private DM into the
	 * project room for everyone with project access to read.
	 *
	 * An open popup is therefore global UNLESS it was opened FOR a room. That
	 * distinction is the whole guard: a room reached this way was named by the
	 * caller, never inferred from the page behind the popup, so the leak the
	 * paragraph above describes is still impossible.
	 */
	const { isOpen: isPopupOpen, room: popupRoom } = useChatPopup()
	const explicitRoom = isPopupOpen ? popupRoom : null
	const chatScope: 'global' | 'room' = isPopupOpen && !popupRoom ? 'global' : 'room'

	useEffect(() => {
		const locationArr = location.pathname.split('/')
		const segmentAfter = (name: string) => {
			const i = locationArr.indexOf(name)
			return i === -1 ? '' : locationArr[i + 1] || ''
		}

		const projectId = segmentAfter('projects')
		// Project scope wins if a path somehow contains both, preserving the
		// behavior of the original projects-only sniff.
		const portfolioId = projectId ? '' : segmentAfter('portfolios')

		setCurrentProjectId(projectId)
		setCurrentPortfolioId(portfolioId)
		// eslint-disable-next-line
	}, [location])

	// @ts-ignore
	const userLogin = useSelector(state => state.userLogin)
	const userId = userLogin?.userInfo?._id

	const [state, dispatch] = useReducer(reducer, STATE)
	const setState = (type: string, payload: any = undefined) => dispatch({ type, payload })
	const _setState = curry(setState) // curried setState
	// create actions
	const _actions = Object.entries(ACTIONS).reduce(
		(acc, curr) => ({ ...acc, [curr[1]]: _setState(curr[1]) }),
		{},
	) as IChatInternalActions

	const {
		setChatMessagesForEvent,
		appendChatMessage,
		prependChatMessages,
		replaceChatMessage,
		replaceMessageById,
		patchChatMessage,
		setCurrentEventId,
		setGlobalEventId,
		setCurrentProjectId,
		setCurrentPortfolioId,
		setCurrentReceiverIds,
		setChatInput,
		setPendingAttachments,
		setStartNewChat,
		setIsLoading,
		setIsSendingMessage,
		setOnlineUserIds,
		setReadStateByEventId,
		setIsSocketConnected,
	} = _actions

	/**
	 * The thread pointer for the scope in view. Computed rather than stored, so
	 * opening and closing the popup restores each side's selection for free.
	 */
	const currentEventId = chatScope === 'global' ? state.globalEventId : state.roomEventId

	// Kept in a ref so socket callbacks, which are bound once per handler
	// identity, can read the live value instead of a stale closure. This
	// replaces the `window.currentEventId` global that did the same job.
	const currentEventIdRef = useRef(currentEventId)
	currentEventIdRef.current = currentEventId

	/**
	 * Headers come from the `chatHeaders` Redux slice, which the popup badge
	 * also reads. Formatting is memoized because it allocates a new object per
	 * header and this runs on every provider render.
	 */
	const rawChatHeaders = useSelector(selectChatHeaders)
	const chatHeaders = useMemo(
		() => formatChatHeadersByUserId(rawChatHeaders as any, userId),
		[rawChatHeaders, userId],
	)

	// One inbox fetch per login, app-wide. This used to be gated on the URL
	// containing '/chats'; the popup opens from everywhere, and the unread
	// badge needs headers before the popup is ever opened.
	useEffect(() => {
		if (userId) reduxDispatch(getChatHeadersByUID() as any)
	}, [userId, reduxDispatch])

	/**
	 * The room currently in scope. Null whenever the popup is open WITHOUT a
	 * room, which is what routes a popup send to the DM branch of
	 * `sendChatMessage` rather than into whatever project page happens to be
	 * behind it.
	 *
	 * A room named by `open({ room })` wins over the URL sniff. The field app
	 * reaches a project conversation that way, and its route
	 * (`/field/project/:projectId`, singular) is not one the sniff can read.
	 */
	const currentRoom: { scope: 'project' | 'portfolio'; id: string; key: string } | null =
		chatScope === 'global'
			? null
			: explicitRoom?.projectId
				? {
						scope: 'project',
						id: explicitRoom.projectId,
						key: explicitRoom.projectId,
					}
				: explicitRoom?.portfolioId
					? {
							scope: 'portfolio',
							id: explicitRoom.portfolioId,
							key: portfolioRoomKey(explicitRoom.portfolioId),
						}
					: state.currentProjectId
				? { scope: 'project', id: state.currentProjectId, key: state.currentProjectId }
				: state.currentPortfolioId
					? {
							scope: 'portfolio',
							id: state.currentPortfolioId,
							key: portfolioRoomKey(state.currentPortfolioId),
						}
					: null

	/**
	 * Takes the `{ header, messages, readState }` a DM thread fetch returns,
	 * stores the read/delivery marks, and hands back just the messages.
	 */
	const absorbThreadResponse = (eventId: string, response: any) => {
		if (!response) return []
		if (response.readState) {
			setReadStateByEventId({ ...state.readStateByEventId, [eventId]: response.readState })
		}
		return response.messages || []
	}

	const getChatMessages = async (
		eventId: string,
		receiverIds: { receiverId: { _id: string } }[] = [],
		shouldUpdateCurrentEventId = true,
	) => {
		if (!state.chatMessages[eventId]) {
			let chatMessages = null

			// The portfolio endpoint can 403. apiCall does not catch and the axios
			// interceptor rejects, so without this the rejection is unhandled and
			// the chat blanks. Do not cache a failure, so a later visit retries.
			try {
				if (currentRoom) {
					chatMessages =
						currentRoom.scope === 'portfolio'
							? await chatService().getChatsByPortfolioId(currentRoom.id)
							: await chatService().getChatsByRoomId(currentRoom.id)
				} else {
					const response = await chatService().getChatsByEventId(eventId)
					chatMessages = absorbThreadResponse(eventId, response)
				}
			} catch (err) {
				console.error('getChatMessages failed', err)
			}

			if (chatMessages) setChatMessagesForEvent({ eventId, messages: chatMessages })
		}
		if (shouldUpdateCurrentEventId) {
			if (chatScope === 'global') setGlobalEventId(eventId)
			else setCurrentEventId(eventId)
		}
		if (receiverIds.length > 0) {
			setCurrentReceiverIds(receiverIds.map(receiver => receiver.receiverId._id))
		}

		setStartNewChat(false)

		setTimeout(() => {
			chatInputRef.current?.focus()
		}, 0)
	}

	/** Re-fetches the inbox. Kept under its old name for existing callers. */
	const getChatHeaders = useCallback(() => {
		if (userId) reduxDispatch(getChatHeadersByUID() as any)
	}, [userId, reduxDispatch])

	/**
	 * Selects a DM thread in the popup. This used to happen implicitly inside
	 * `getChatHeaders`, which force-selected the first conversation; iOS opens
	 * to the list, so selection is now an explicit act.
	 */
	const openThread = async (eventId: string, receiverIds: any[] = []) => {
		setGlobalEventId(eventId)
		await getChatMessages(eventId, receiverIds)
		if (userId) reduxDispatch(chatHeaderMarkedRead({ eventId, userId }))
	}

	/** Fetches the page of messages before the oldest one currently held. */
	const loadEarlierMessages = async (eventId: string) => {
		const existing = state.chatMessages[eventId] || []
		const oldest = existing[0]
		if (!oldest?.createdAt) return

		try {
			const response = await chatService().getChatsByEventId(eventId, { before: oldest.createdAt })
			const older = response?.messages || []
			if (older.length === 0) return
			// The reducer dedupes on _id: the header document joins the stream on
			// the page that reaches the beginning of history, and a boundary
			// message can repeat if one arrived between pages.
			prependChatMessages({ eventId, messages: older })
		} catch (err) {
			console.error('loadEarlierMessages failed', err)
		}
	}

	const handleStartNewChat = () => {
		setStartNewChat(true)
		if (chatScope === 'global') setGlobalEventId('')
		else setCurrentEventId('')
		setCurrentReceiverIds([])
		setChatInput('')

		setTimeout(() => {
			autocompleteInputRef.current?.focus()
		}, 0)
	}

	const handleCloseNewChat = () => {
		setStartNewChat(false)
		setCurrentReceiverIds([])
		setChatInput('')

		if (chatHeaders.length > 0) {
			getChatMessages(chatHeaders[0]._id, chatHeaders[0].receiverIds)
		}
	}

	const handleChatInput = e => {
		setChatInput(e.target.value)
	}

	// Keep the ref in step with state on every render.
	pendingAttachmentsRef.current = state.pendingAttachments || []

	/** Swaps one pending row for its uploaded (or failed) replacement. */
	const replacePendingAttachment = (tempId: string, next: any) => {
		const list = pendingAttachmentsRef.current.map(a => (a.tempId === tempId ? next : a))
		pendingAttachmentsRef.current = list
		setPendingAttachments(list)
	}

	/**
	 * Uploads the chosen files and holds them as pending attachments until the
	 * message is sent.
	 *
	 * Each file goes into the list immediately with a local object URL so the
	 * preview appears while the upload is still running, then is replaced in
	 * place by the uploaded record. A failed upload stays in the list marked
	 * with an error rather than vanishing silently.
	 */
	const handleAddAttachments = async (
		files: File[],
		context: { locationId?: string; projectId?: string } = {},
	) => {
		if (!files?.length) return

		const pending = files.map(file => ({
			tempId: newClientId(),
			url: '',
			name: file.name,
			size: file.size,
			mimeType: file.type || '',
			kind: kindForFile(file),
			previewUrl: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
			uploading: true,
		}))

		const withPending = [...pendingAttachmentsRef.current, ...pending]
		pendingAttachmentsRef.current = withPending
		setPendingAttachments(withPending)

		await Promise.all(
			files.map(async (file, i) => {
				const tempId = pending[i].tempId
				try {
					const uploaded = await uploadChatAttachment(file, context)
					replacePendingAttachment(tempId, { ...uploaded, tempId, previewUrl: pending[i].previewUrl })
				} catch (error: any) {
					replacePendingAttachment(tempId, {
						...pending[i],
						uploading: false,
						error: error?.message || 'Upload failed',
					})
				}
			}),
		)
	}

	const handleRemoveAttachment = (tempId: string) => {
		const removed = pendingAttachmentsRef.current.find(a => a.tempId === tempId)
		if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl)
		const next = pendingAttachmentsRef.current.filter(a => a.tempId !== tempId)
		pendingAttachmentsRef.current = next
		setPendingAttachments(next)
	}

	/**
	 * Inserts an emoji at the caret rather than appending to the end, so it
	 * still lands in the right place when the user has clicked back into the
	 * middle of a half-written message.
	 *
	 * Falls back to appending when the textarea is not mounted or exposes no
	 * selection, which is the case in tests and in the fixture-driven
	 * ChatWindow preview.
	 */
	const handleInsertEmoji = (native: string) => {
		if (!native) return

		const el = chatInputRef.current as unknown as HTMLTextAreaElement | null
		const current = state.chatInput || ''

		if (!el || el.selectionStart === null || el.selectionStart === undefined) {
			setChatInput(current + native)
			return
		}

		const start = el.selectionStart
		const end = el.selectionEnd ?? start
		setChatInput(current.slice(0, start) + native + current.slice(end))

		// React re-renders from state, which resets the caret to the end of the
		// value. Restore it after that render so the user can keep typing where
		// they left off.
		const caret = start + native.length
		requestAnimationFrame(() => {
			el.focus()
			el.setSelectionRange(caret, caret)
		})
	}

	const sendChatMessage = async () => {
		// Only attachments that finished uploading can be sent. Anything still in
		// flight or failed is left in the tray rather than silently dropped.
		const readyAttachments = (state.pendingAttachments || [])
			.filter(a => a.url && !a.uploading && !a.error)
			.map(({ previewUrl, uploading, error, tempId, ...rest }) => rest)

		// A message may be attachments only, so this no longer requires text.
		if (!state.chatInput && readyAttachments.length === 0) return

		const clearAttachments = () => {
			;(state.pendingAttachments || []).forEach(a => {
				if (a.previewUrl) URL.revokeObjectURL(a.previewUrl)
			})
			pendingAttachmentsRef.current = []
			setPendingAttachments([])
		}

		if (currentRoom) {
			const chatMessageData = {
				projectId: currentRoom.scope === 'project' ? currentRoom.id : undefined,
				roomId: currentRoom.scope === 'portfolio' ? currentRoom.key : undefined,
				eventId: undefined,
				receiverIds: [],
				message: state.chatInput,
				attachments: readyAttachments,
			}

			const chatMessage = await chatService().addChatEvent(chatMessageData)
			socket.emit('send_message', chatMessage)

			// Key off the room descriptor rather than the echoed document. For
			// projects currentRoom.key === chatMessage.projectid, so this is
			// behavior-identical on the path that was live.
			appendChatMessage({ eventId: currentRoom.key, message: { ...chatMessage } })

			setChatInput('')
			clearAttachments()
			setIsLoading(false)
			setIsSendingMessage(false)
		} else {
			setIsSendingMessage(true)
			let isFirstMessage = !currentEventId

			if (isFirstMessage) {
				setStartNewChat(false)
				setIsLoading(true)
			}

			const existingEventIdForChatMessage = getEventIdForReceiverIds(
				chatHeaders,
				state.currentReceiverIds,
				userId,
			)

			const clientId = newClientId()
			const chatMessageData = {
				eventId: isFirstMessage ? existingEventIdForChatMessage : currentEventId,
				receiverIds: state.currentReceiverIds,
				message: state.chatInput,
				attachments: readyAttachments,
				clientId,
			}

			// Show the bubble before the round trip, then reconcile on the
			// response. A send that fails leaves the bubble in place marked
			// failed, rather than vanishing with the text in it.
			const optimisticEventId = chatMessageData.eventId || currentEventId
			if (optimisticEventId) {
				const optimistic: any = {
					_id: clientId,
					clientId,
					eventId: optimisticEventId,
					createdAt: new Date().toISOString(),
					message: state.chatInput,
					senderId: { _id: userId, name: userLogin?.userInfo?.name, image: userLogin?.userInfo?.image },
					receiverIds: [],
					attachments: readyAttachments,
					deliveryStatus: 'sending',
				}
				appendChatMessage({ eventId: optimisticEventId, message: optimistic })
			}

			setChatInput('')
			clearAttachments()

			let chatMessage
			try {
				chatMessage = await chatService().addChatEvent(chatMessageData)
			} catch (err) {
				console.error('sendChatMessage failed', err)
				if (optimisticEventId) {
					patchChatMessage({
						eventId: optimisticEventId,
						clientId,
						patch: { deliveryStatus: 'failed' },
					})
				}
				setIsLoading(false)
				setIsSendingMessage(false)
				return
			}

			const eventId = !existingEventIdForChatMessage ? chatMessage._id : chatMessage.eventId

			if (!existingEventIdForChatMessage) {
				// First message of a brand new thread: the message document IS
				// the header, so it is the whole thread.
				setChatMessagesForEvent({
					eventId,
					messages: [{ ...chatMessage, deliveryStatus: 'sent' }],
				})
				// Point the popup at the thread that was just created. Without
				// this the pointer stays empty and the composer sends into a
				// conversation the user cannot see.
				setGlobalEventId(eventId)
				setCurrentReceiverIds(state.currentReceiverIds)
			} else {
				replaceChatMessage({
					eventId,
					clientId,
					message: { ...chatMessage, deliveryStatus: 'sent' },
				})
			}

			socket.emit('send_message', chatMessage)

			getChatHeaders()
			setIsLoading(false)
			setIsSendingMessage(false)
		}
	}

	const handleSendChatMessage = e => {
		e.preventDefault()
		sendChatMessage()
	}

	/**
	 * Re-POSTs a failed message. The original clientId goes back out, so if the
	 * first attempt actually reached the server and only the response was lost,
	 * the server returns that message instead of writing a second one.
	 */
	const retrySendMessage = async (clientId: string) => {
		const eventId = currentEventIdRef.current
		const existing = state.chatMessages[eventId] || []
		const failed = existing.find(m => m.clientId === clientId)
		if (!failed) return

		patchChatMessage({ eventId, clientId, patch: { deliveryStatus: 'sending' } })

		try {
			const chatMessage = await chatService().addChatEvent({
				eventId,
				receiverIds: state.currentReceiverIds,
				message: failed.message,
				attachments: failed.attachments,
				clientId,
			})
			replaceChatMessage({
				eventId,
				clientId,
				message: { ...chatMessage, deliveryStatus: 'sent' },
			})
			socket.emit('send_message', chatMessage)
			getChatHeaders()
		} catch (err) {
			console.error('retrySendMessage failed', err)
			patchChatMessage({ eventId, clientId, patch: { deliveryStatus: 'failed' } })
		}
	}

	/**
	 * Rewrites the text of a message. The server enforces that it is yours and
	 * that it is the most recent one you sent in the thread; the UI only offers
	 * the affordance on that message, so a rejection here means the thread moved
	 * on underneath the user.
	 */
	const editMessage = async (messageId: string, message: string) => {
		const eventId = currentEventIdRef.current
		try {
			const updated = await chatService().editMessage(messageId, message)
			replaceMessageById({ eventId, message: updated })
			socket.emit('update_message', updated)
			getChatHeaders()
		} catch (err) {
			console.error('editMessage failed', err)
			throw err
		}
	}

	/** Sets, changes or clears the current user's reaction on a message. */
	const toggleReaction = async (messageId: string, emoji: string) => {
		const eventId = currentEventIdRef.current
		try {
			const updated = await chatService().reactToMessage(messageId, emoji)
			replaceMessageById({ eventId, message: updated })
			socket.emit('update_message', updated)
		} catch (err) {
			console.error('toggleReaction failed', err)
		}
	}

	const handleJoinChatRoom = (
		username: string,
		roomId: string,
		userId: undefined | string = undefined,
	) => {
		if (username && roomId) {
			socket.emit('join_room', { username, roomId, userId })
		}
	}

	const joinChatRoom = useCallback(() => {
		const username = userLogin?.userInfo?.name
		handleJoinChatRoom(username, currentEventId, userLogin?.userInfo?._id)
		// eslint-disable-next-line
	}, [currentEventId, userLogin])

	/**
	 * Joins the global room (presence only), the user's own room, and one room
	 * per thread, so a message arrives even when its thread is not the one on
	 * screen. Runs off the header list rather than off the fetch that produced
	 * it, so threads created mid-session are joined too.
	 *
	 * The user's own room is where the first message of a new conversation
	 * arrives (MYD-649). It used to go to the global room, which reached every
	 * signed-in user.
	 */
	useEffect(() => {
		const username = userLogin?.userInfo?.name
		if (!username) return

		if (!joinedChatRooms.current.has('GLOBAL_CHAT_ROOM')) {
			handleJoinChatRoom(username, 'GLOBAL_CHAT_ROOM')
			joinedChatRooms.current.add('GLOBAL_CHAT_ROOM')
		}

		const ownRoom = userId ? userChatRoomKey(userId) : ''
		if (ownRoom && !joinedChatRooms.current.has(ownRoom)) {
			handleJoinChatRoom(username, ownRoom)
			joinedChatRooms.current.add(ownRoom)
		}

		chatHeaders.forEach(header => {
			if (joinedChatRooms.current.has(header._id)) return
			handleJoinChatRoom(username, header._id)
			joinedChatRooms.current.add(header._id)
		})
		// socketGeneration is in the deps so a reconnect re-joins every room.
		// socket.io does not restore room membership itself, so without this a
		// dropped connection silently stops delivering messages, typing and
		// receipts until the page is reloaded.
	}, [chatHeaders, userLogin, userId, socketGeneration])

	/**
	 * The socket is created at module load, before login, with whatever token
	 * localStorage happened to hold. Reconnect when the signed-in user changes
	 * so the handshake carries the current token; otherwise a user who logs in
	 * during a session that started logged-out holds an unauthenticated socket
	 * and silently receives nothing once the chat server enforces JWT_SECRET.
	 */
	useEffect(() => {
		if (!userId) return
		socket.disconnect().connect()
		joinedChatRooms.current = new Set()
	}, [userId])

	useEffect(() => {
		const onConnect = () => {
			setIsSocketConnected(true)
			// Rooms do not survive a reconnect. Forget what we joined and bump
			// the generation so the join effect above runs again.
			joinedChatRooms.current = new Set()
			setSocketGeneration(g => g + 1)
		}
		const onDisconnect = () => setIsSocketConnected(false)
		socket.on('connect', onConnect)
		socket.on('disconnect', onDisconnect)
		return () => {
			socket.off('connect', onConnect)
			socket.off('disconnect', onDisconnect)
		}
		// eslint-disable-next-line
	}, [])

	const handleUserJoinedRoom = useCallback(
		userId => {
			setOnlineUserIds(Array.from(new Set([...state.onlineUserIds, userId])))
		},
		// eslint-disable-next-line
		[state.onlineUserIds],
	)

	const handleUserLeftRoom = useCallback(
		userId => {
			setOnlineUserIds(state.onlineUserIds.filter(id => id !== userId))
		},
		// eslint-disable-next-line
		[state.onlineUserIds],
	)

	const handleReceiveMessage = useCallback(
		message => {
			// Project messages carry neither roomId nor eventId, so the old
			// `roomId || eventId` derivation gave '' and they were dropped here
			// as well as mis-routed by the relay.
			const eventId = cacheKeyForMessage(message)

			// A message from the GLOBAL_CHAT_ROOM opening a thread we have never
			// seen. Only then is a full inbox refetch worth a round trip.
			const receiverIds = (message.receiverIds || []).map(receiver => receiver.receiverId)

			if (state.chatMessages[eventId]) {
				// The reducer skips the echo of a message this client already
				// appended, matching on clientId and _id.
				appendChatMessage({ eventId, message: { ...message } })
			} else if (receiverIds.includes(userId)) {
				getChatHeaders()
			} else if (eventId) {
				appendChatMessage({ eventId, message: { ...message } })
			}

			if (message.eventId) {
				// Update the inbox row in place rather than refetching the whole
				// inbox on every received message. The reducer merges, so the
				// fields the socket payload does not carry are preserved.
				const known = rawChatHeaders.some((h: any) => h._id === message.eventId)
				if (known) {
					reduxDispatch(
						chatHeaderUpserted({
							_id: message.eventId,
							updatedAt: message.createdAt,
							lastMessage: {
								senderId: message.senderId,
								message:
									message.message ||
									`${message.attachments?.length || 0} attachment${
										message.attachments?.length === 1 ? '' : 's'
									}`,
							},
							// The sender has read their own message; nobody else has.
							readByIds: [String(message.senderId?._id || message.senderId)],
						} as any),
					)
				} else {
					getChatHeaders()
				}

				// Receiving a message is proof of delivery. Report it both ways:
				// the socket so an online sender flips within a frame, the POST
				// so it survives everyone going offline. Best effort by design —
				// the chat server holds no state, and a missed report is caught
				// by the server-side sweep next time the thread is opened.
				chatService()
					.markThreadDelivered(message.eventId, message._id)
					.catch(() => {})
				socket.emit('message_delivered', {
					roomId: message.eventId,
					eventId: message.eventId,
					userId,
					lastDeliveredMessageId: message._id,
					at: new Date().toISOString(),
				})
			}
		},
		// eslint-disable-next-line
		[state.chatMessages, userId, getChatHeaders, rawChatHeaders, reduxDispatch],
	)

	/** Merges one participant's read/delivery marks into the thread's readState. */
	const mergeReadState = useCallback(
		(eventId: string, row: Partial<IReadStateRow> & { userId: string }) => {
			const existing = state.readStateByEventId[eventId] || []
			const next = existing.some(r => String(r.userId) === String(row.userId))
				? existing.map(r => (String(r.userId) === String(row.userId) ? { ...r, ...row } : r))
				: [...existing, row as IReadStateRow]
			setReadStateByEventId({ ...state.readStateByEventId, [eventId]: next })
		},
		// eslint-disable-next-line
		[state.readStateByEventId],
	)

	useEffect(() => {
		socket.off('receive_message').on('receive_message', message => {
			handleReceiveMessage(message)
		})
	}, [handleReceiveMessage])

	useEffect(() => {
		socket.off('message_updated').on('message_updated', message => {
			if (!message?._id) return
			replaceMessageById({ eventId: cacheKeyForMessage(message), message })
		})
		// eslint-disable-next-line
	}, [])

	useEffect(() => {
		socket.off('messages_read').on('messages_read', payload => {
			if (!payload?.eventId || !payload?.userId) return
			mergeReadState(payload.eventId, {
				userId: payload.userId,
				lastReadMessageId: payload.lastReadMessageId,
				lastReadAt: payload.at,
			})
		})
		socket.off('messages_delivered').on('messages_delivered', payload => {
			if (!payload?.eventId || !payload?.userId) return
			mergeReadState(payload.eventId, {
				userId: payload.userId,
				lastDeliveredMessageId: payload.lastDeliveredMessageId,
				lastDeliveredAt: payload.at,
			})
		})
	}, [mergeReadState])

	useEffect(() => {
		socket.off('user_is_online').on('user_is_online', userId => {
			handleUserJoinedRoom(userId)
		})
	})

	useEffect(() => {
		socket.off('user_is_offline').on('user_is_offline', userId => {
			handleUserLeftRoom(userId)
		})
	})

	useEffect(() => {
		scrollToBottom()
	}, [state.chatMessages])

	useEffect(() => {
		joinChatRoom()
		scrollToBottom('instant')
		// eslint-disable-next-line
	}, [currentEventId])

	/**
	 * Loads a room's history.
	 *
	 * Keyed on the room, because that is what it fetches. Keyed on
	 * `currentEventId` alone, a load of /projects/:id/chat could settle the
	 * thread pointer in the same commit that the URL sniff resolves the project,
	 * leaving the effect's closure holding a null room and never firing again,
	 * so the conversation came up empty. getChatMessages caches, so a repeat
	 * run costs nothing.
	 */
	useEffect(() => {
		if (currentRoom) getChatMessages(currentRoom.key)
		// eslint-disable-next-line
	}, [currentRoom?.key])

	/**
	 * Empties the composer when the user moves to a different conversation.
	 *
	 * Guarded on the scope being unchanged. Opening or closing the popup swaps
	 * which pointer is live without either conversation changing, and clearing
	 * on that wipes a half-typed message in the pane the user just left.
	 */
	const lastComposerThread = useRef<{ scope: string; id: string } | null>(null)
	useEffect(() => {
		const previous = lastComposerThread.current
		lastComposerThread.current = { scope: chatScope, id: currentEventId }

		// Requires a real previous thread. A pointer settling from '' to its id
		// is the same conversation acquiring its key, not a move between two,
		// and clearing on it wiped whatever had been typed while the room's
		// history was still loading.
		const movedBetweenThreads =
			!!previous?.id && !!currentEventId && previous.id !== currentEventId

		if (previous && previous.scope === chatScope && movedBetweenThreads) {
			setChatInput('')
		}
		// eslint-disable-next-line
	}, [currentEventId, chatScope])

	/**
	 * Records who a new conversation is addressed to.
	 *
	 * Stores ids, not the partner documents. GET /api/mypartners populates
	 * `partner`, so this used to hold whole user objects. Mongoose casts those
	 * on save, which is why sending appeared to work, but every consumer that
	 * treats a receiver as an id got the string "[object Object]": the
	 * existing-thread matcher never matched, so the second message in a new
	 * conversation moved the pointer to that message's own id rather than the
	 * thread's, and typing and receipts were then emitted to a room nobody is
	 * in.
	 */
	const handleAutocomplete = (event, value) => {
		const newReceiverIds = (value || [])
			.map(receiver => receiver?.partner?._id || receiver?.partner)
			.filter(Boolean)
			.map(String)
		setCurrentReceiverIds(newReceiverIds)
	}

	const scrollToBottom = (behavior = 'smooth') => {
		messagesEndRef.current?.scrollIntoView({
			// @ts-ignore
			behavior,
			block: 'nearest',
			inline: 'start',
		})
	}

	const currentMessages = state.chatMessages[`${currentEventId}`]
	const hasCurrentMessages = currentEventId && currentMessages && currentMessages.length > 0
	const shouldDisplayChatInput = state.startNewChat || hasCurrentMessages
	const currentChatHeader = chatHeaders.find(chatHeader => chatHeader._id === currentEventId)

	const chatActions: IChatActions = {
		..._actions,
		getChatHeaders,
		getChatMessages,
		openThread,
		loadEarlierMessages,
		retrySendMessage,
		editMessage,
		toggleReaction,
		handleStartNewChat,
		handleCloseNewChat,
		handleChatInput,
		handleInsertEmoji,
		handleAddAttachments,
		handleRemoveAttachment,
		handleSendChatMessage,
		handleAutocomplete,
	}

	const chatState: IChatState = {
		...state,
		userId,
		chatHeaders,
		chatInputRef,
		autocompleteInputRef,
		messagesEndRef,

		// computed state
		chatScope,
		currentEventId,
		currentMessages,
		hasCurrentMessages,
		shouldDisplayChatInput,
		currentChatHeader,
	}

	return (
		<ChatContext.Provider value={{ state: chatState, actions: chatActions }}>
			{children}
		</ChatContext.Provider>
	)
}

const useChat = () => {
	const context = React.useContext(ChatContext)
	if (context === undefined) throw new Error('useChat must be used within a ChatProvider')
	return context
}

export { ChatProvider, useChat }

/*
  # Usage
  import { useChat } from 'features/Chat'
  const { state, actions } = useChat()
*/
